import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { fileUrl } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Standings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/standings`);
        setData(res.data);
      } catch (e) {
        setData({ league: "Midtnorsk Hockeyliga", standings: [], results: [], upcoming: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="text-sm uppercase tracking-widest text-neutral-500">Laster…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-black flex items-center justify-center font-heading font-black text-xl">M</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-300">Sesongoversikt</div>
              <div className="font-heading text-xl font-black uppercase tracking-tight text-white">{data.league}</div>
            </div>
          </div>
          <Link to="/" className="text-xs uppercase tracking-widest text-neutral-200 hover:text-white">Logg inn →</Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Standings */}
        <section className="lg:col-span-2">
          <div className="flex items-baseline justify-between mb-6">
            <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tighter text-white">Tabell</h1>
            <div className="text-xs uppercase tracking-widest text-neutral-300">Sesong 2026</div>
          </div>

          {data.standings.length === 0 ? (
            <div className="border border-neutral-700 p-12 text-center text-neutral-300">Ingen kamper spilt ennå</div>
          ) : (
            <div className="border border-neutral-700 overflow-x-auto">
              <table className="w-full text-sm" data-testid="standings-table">
                <thead className="bg-neutral-900 border-b border-neutral-700">
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-neutral-200">
                    <th className="px-4 py-3 w-12 text-center">#</th>
                    <th className="px-4 py-3">Lag</th>
                    <th className="px-3 py-3 text-center w-12">GP</th>
                    <th className="px-3 py-3 text-center w-12">V</th>
                    <th className="px-3 py-3 text-center w-12">T</th>
                    <th className="px-3 py-3 text-center w-12">OTL</th>
                    <th className="px-3 py-3 text-center w-12">M+</th>
                    <th className="px-3 py-3 text-center w-12">M-</th>
                    <th className="px-3 py-3 text-center w-12">DIFF</th>
                    <th className="px-3 py-3 text-center w-14">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.standings.map((s, i) => (
                    <tr key={s.team_id} className="border-b border-neutral-800 hover:bg-neutral-900 text-white" data-testid={`standings-row-${s.team_id}`}>
                      <td className="px-4 py-4 text-center">
                        <div className="inline-flex items-center justify-center w-8 h-8 font-heading font-black text-lg" style={{ color: s.accent_color || "#fff" }}>
                          {s.rank}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 border border-neutral-700 bg-neutral-800 flex items-center justify-center overflow-hidden">
                            {s.logo_path ? (
                              <img src={fileUrl(s.logo_path)} alt={s.name} className="w-full h-full object-contain" />
                            ) : (
                              <span className="font-heading font-black text-neutral-300">{s.name[0]}</span>
                            )}
                          </div>
                          <div className="font-heading font-bold uppercase tracking-tight text-base">{s.name}</div>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-center font-mono">{s.gp}</td>
                      <td className="px-3 py-4 text-center font-mono">{s.w}</td>
                      <td className="px-3 py-4 text-center font-mono text-neutral-300">{s.l}</td>
                      <td className="px-3 py-4 text-center font-mono text-neutral-300">{s.otl}</td>
                      <td className="px-3 py-4 text-center font-mono">{s.gf}</td>
                      <td className="px-3 py-4 text-center font-mono text-neutral-300">{s.ga}</td>
                      <td className={`px-3 py-4 text-center font-mono font-bold ${s.diff > 0 ? "text-emerald-300" : s.diff < 0 ? "text-rose-300" : "text-neutral-300"}`}>
                        {s.diff > 0 ? "+" : ""}{s.diff}
                      </td>
                      <td className="px-3 py-4 text-center font-heading font-black text-xl">{s.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 text-[10px] uppercase tracking-widest text-neutral-300 flex flex-wrap gap-x-6 gap-y-1">
            <span>GP = Spilte</span>
            <span>V = Seier (2p)</span>
            <span>T = Tap</span>
            <span>OTL = OT-tap (1p)</span>
            <span>M+/- = Mål for/mot</span>
            <span>PTS = Poeng</span>
          </div>
        </section>

        {/* Right rail: Latest results + Upcoming */}
        <aside className="space-y-10">
          <div>
            <h2 className="font-heading text-2xl font-black uppercase tracking-tight mb-4 text-white">Siste resultater</h2>
            <div className="space-y-2">
              {data.results.length === 0 && (
                <div className="border border-neutral-700 p-6 text-center text-neutral-300 text-sm">Ingen resultater ennå</div>
              )}
              {data.results.map((r) => (
                <ResultCard key={r.id} result={r} />
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-black uppercase tracking-tight mb-4 text-white">Kommende kamper</h2>
            <div className="space-y-2">
              {data.upcoming.length === 0 && (
                <div className="border border-neutral-700 p-6 text-center text-neutral-300 text-sm">Ingen kommende kamper</div>
              )}
              {data.upcoming.map((m) => (
                <UpcomingCard key={m.id} match={m} />
              ))}
            </div>
          </div>
        </aside>
      </main>

      <footer className="border-t border-neutral-800 mt-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 text-xs uppercase tracking-widest text-neutral-300 flex justify-between">
          <span>© {new Date().getFullYear()} Midtnorsk Hockeyliga</span>
          <Link to="/" className="hover:text-white">Innlogging</Link>
        </div>
      </footer>
    </div>
  );
}

function TeamRow({ name, logo, isWinner }) {
  return (
    <div className={`flex items-center gap-3 ${isWinner ? "text-white" : "text-neutral-400"}`}>
      <div className="w-8 h-8 border border-neutral-700 bg-neutral-800 flex items-center justify-center overflow-hidden">
        {logo ? <img src={fileUrl(logo)} alt={name} className="w-full h-full object-contain" /> : <span className="font-heading font-black text-neutral-300 text-xs">{name?.[0] || "?"}</span>}
      </div>
      <div className="font-heading font-bold uppercase text-sm truncate flex-1">{name || "—"}</div>
    </div>
  );
}

function ResultCard({ result }) {
  const homeWon = result.home_score > result.away_score;
  const awayWon = result.away_score > result.home_score;
  return (
    <div className="border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 transition" data-testid={`result-${result.id}`}>
      <div className="px-3 py-2 border-b border-neutral-700 flex justify-between text-[10px] uppercase tracking-widest text-neutral-300">
        <span>{result.title}</span>
        <span>{new Date(result.match_date).toLocaleDateString("nb-NO", { day: "2-digit", month: "short" })}</span>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <TeamRow name={result.home_team_name} logo={result.home_team_logo} isWinner={homeWon} />
          <div className={`font-heading font-black text-2xl tabular-nums ${homeWon ? "text-white" : "text-neutral-400"}`}>{result.home_score}</div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <TeamRow name={result.away_team_name} logo={result.away_team_logo} isWinner={awayWon} />
          <div className={`font-heading font-black text-2xl tabular-nums ${awayWon ? "text-white" : "text-neutral-400"}`}>{result.away_score}</div>
        </div>
        {result.overtime && <div className="text-[10px] uppercase tracking-widest text-amber-300">OT / Straffer</div>}
      </div>
    </div>
  );
}

function UpcomingCard({ match }) {
  return (
    <div className="border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 transition" data-testid={`upcoming-${match.id}`}>
      <div className="px-3 py-2 border-b border-neutral-700 flex justify-between text-[10px] uppercase tracking-widest text-neutral-300">
        <span>{match.title}</span>
        <span>{new Date(match.match_date).toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <div className="p-3 space-y-2">
        <TeamRow name={match.home_team_name} logo={match.home_team_logo} isWinner={true} />
        <div className="text-[10px] uppercase tracking-widest text-neutral-300 pl-11">vs.</div>
        <TeamRow name={match.away_team_name} logo={match.away_team_logo} isWinner={true} />
        {match.venue && <div className="text-[10px] uppercase tracking-widest text-neutral-300 pt-1">{match.venue}</div>}
      </div>
    </div>
  );
}
